"use client";

import { useState, type KeyboardEvent } from "react";
import { HiDownload, HiClipboardCopy, HiCheck, HiExclamation, FaYoutube } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

interface ThumbnailQuality {
  key: string;
  label: string;
  dimensions: string;
  filenameSuffix: string;
}

const QUALITIES: ThumbnailQuality[] = [
  {
    key: "maxresdefault",
    label: "Max Resolution (HD)",
    dimensions: "1280×720",
    filenameSuffix: "maxres",
  },
  { key: "sddefault", label: "Standard Definition", dimensions: "640×480", filenameSuffix: "sd" },
  { key: "hqdefault", label: "High Quality", dimensions: "480×360", filenameSuffix: "hq" },
  { key: "mqdefault", label: "Medium Quality", dimensions: "320×180", filenameSuffix: "mq" },
  { key: "default", label: "Default", dimensions: "120×90", filenameSuffix: "default" },
];

const VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

function extractVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (VIDEO_ID_PATTERN.test(trimmed)) return trimmed;

  try {
    const url = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`);
    const host = url.hostname.replace(/^(www\.|m\.)/, "");

    if (host === "youtu.be") {
      const id = url.pathname.slice(1).split("/")[0] ?? "";
      if (VIDEO_ID_PATTERN.test(id)) return id;
    }

    if (host === "youtube.com" || host === "youtube-nocookie.com") {
      const v = url.searchParams.get("v");
      if (v && VIDEO_ID_PATTERN.test(v)) return v;

      const parts = url.pathname.split("/").filter(Boolean);
      // /shorts/ID, /embed/ID, /v/ID, /live/ID
      if (parts.length >= 2 && ["shorts", "embed", "v", "live"].includes(parts[0])) {
        if (VIDEO_ID_PATTERN.test(parts[1])) return parts[1];
      }
    }
  } catch {
    // not a URL — fall through
  }

  // Last resort: any 11-char ID substring
  const match = trimmed.match(/[a-zA-Z0-9_-]{11}/);
  return match ? match[0] : null;
}

function thumbnailUrl(videoId: string, quality: string): string {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

async function downloadImage(
  url: string,
  filename: string
): Promise<{ ok: boolean; fallback?: string }> {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) {
      window.open(url, "_blank", "noopener");
      return { ok: false, fallback: "Opened in a new tab — right-click the image to save it." };
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    return { ok: true };
  } catch {
    window.open(url, "_blank", "noopener");
    return { ok: false, fallback: "Opened in a new tab — right-click the image to save it." };
  }
}

const FAQ_ITEMS = [
  {
    question: "Why isn't the 1280×720 (maxres) thumbnail available?",
    answer:
      "YouTube only generates the maxresdefault thumbnail for videos uploaded with sufficient source resolution. Older videos or those uploaded in low resolution will only have SD, HQ, MQ, and default. The tool detects this automatically and shows when maxres is unavailable.",
  },
  {
    question: "Does this work for YouTube Shorts?",
    answer:
      "Yes. Paste any youtube.com/shorts/VIDEO_ID URL and the tool extracts the video ID the same way. The same thumbnail qualities are available for Shorts as for regular videos.",
  },
  {
    question: "What URL formats are supported?",
    answer:
      "youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID, youtube.com/embed/ID, youtube.com/v/ID, youtube.com/live/ID, youtube-nocookie.com URLs, m.youtube.com mobile URLs, and raw 11-character video IDs.",
  },
  {
    question: "Can I download thumbnails for many videos at once?",
    answer:
      "This tool focuses on a single video at a time so you can pick the exact quality you need. For bulk downloads, the URL pattern is consistent: https://img.youtube.com/vi/VIDEO_ID/maxresdefault.jpg — script it with curl, wget, or a Chrome extension if you need batches.",
  },
  {
    question: "Is this legal? Can I reuse YouTube thumbnails?",
    answer:
      "YouTube thumbnails live on Google's public CDN and downloading them is technically permitted. Reuse is a separate question — fair use covers commentary, criticism, and research, but commercial reuse generally needs the original creator's permission. Always check the source video's license before publishing.",
  },
  {
    question: "Why does the download open in a new tab instead of saving?",
    answer:
      "Some browsers or network policies block cross-origin downloads. The tool falls back to opening the image in a new tab — right-click and choose 'Save image as' to save it. No data leaves your browser either way.",
  },
];

export default function YoutubeThumbnailDownloaderClient() {
  const [input, setInput] = useState("");
  const [videoId, setVideoId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [maxresAvailable, setMaxresAvailable] = useState<boolean | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);

  const handleExtract = () => {
    setError(null);
    setFallbackMessage(null);
    const id = extractVideoId(input);
    if (!id) {
      setError(
        "Couldn't find a YouTube video ID in that input. Paste a full YouTube URL or the 11-character video ID."
      );
      setVideoId(null);
      setMaxresAvailable(null);
      return;
    }
    setVideoId(id);
    setMaxresAvailable(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleExtract();
  };

  const copyUrl = async (url: string, key: string) => {
    await navigator.clipboard.writeText(url);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const downloadOne = async (q: ThumbnailQuality) => {
    if (!videoId) return;
    const result = await downloadImage(
      thumbnailUrl(videoId, q.key),
      `${videoId}-${q.filenameSuffix}.jpg`
    );
    if (result.fallback) setFallbackMessage(result.fallback);
  };

  const downloadAll = async () => {
    if (!videoId) return;
    setDownloadingAll(true);
    setFallbackMessage(null);
    for (const q of QUALITIES) {
      if (q.key === "maxresdefault" && maxresAvailable === false) continue;
      const result = await downloadImage(
        thumbnailUrl(videoId, q.key),
        `${videoId}-${q.filenameSuffix}.jpg`
      );
      if (result.fallback) setFallbackMessage(result.fallback);
      // small stagger to avoid browser deduping rapid downloads
      await new Promise((r) => setTimeout(r, 300));
    }
    setDownloadingAll(false);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="YouTube Thumbnail Downloader"
        description="Paste any YouTube URL to download its thumbnail in HD (1280×720), SD, HQ, and MQ. Works with Shorts, embed URLs, and raw video IDs."
        url="https://plugmonkey.xyz/tool/youtube-thumbnail-downloader/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "YouTube Thumbnail Downloader",
            url: "https://plugmonkey.xyz/tool/youtube-thumbnail-downloader/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-red-500/10 rounded-full text-red-500">
          <FaYoutube className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">YouTube Thumbnail Downloader</h1>
          <p className="text-muted-foreground mt-1">
            Paste a YouTube URL to download its thumbnails in every available quality.
          </p>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>YouTube URL or Video ID</CardTitle>
          <CardDescription>
            Works with youtube.com/watch, youtu.be, youtube.com/shorts, embed URLs, and raw video
            IDs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              id="youtube-url"
              name="youtube-url"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
              className="flex-1 px-4 py-3 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label="YouTube video URL or ID"
            />
            <Button onClick={handleExtract}>Get Thumbnails</Button>
          </div>
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-500">
              <HiExclamation className="w-5 h-5 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {videoId && !error && (
            <p className="text-sm text-muted-foreground">
              Detected video ID:{" "}
              <code className="px-1.5 py-0.5 rounded bg-muted font-mono">{videoId}</code>
            </p>
          )}
        </CardContent>
      </Card>

      {videoId && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <h2 className="text-2xl font-bold">Available Thumbnails</h2>
            <Button onClick={downloadAll} disabled={downloadingAll}>
              <HiDownload className="w-4 h-4" />
              {downloadingAll ? "Downloading…" : "Download all"}
            </Button>
          </div>

          {fallbackMessage && (
            <div className="text-sm text-muted-foreground p-3 rounded-md border border-border bg-muted/30">
              {fallbackMessage}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {QUALITIES.map((q) => {
              const url = thumbnailUrl(videoId, q.key);
              const isUnavailable = q.key === "maxresdefault" && maxresAvailable === false;
              return (
                <Card key={q.key} className={isUnavailable ? "opacity-50" : ""}>
                  <CardContent className="p-4">
                    <div className="relative bg-muted rounded-md overflow-hidden mb-3 aspect-video">
                      <img
                        src={url}
                        alt={`YouTube thumbnail — ${q.label}`}
                        className="w-full h-full object-cover"
                        onLoad={(e) => {
                          if (q.key === "maxresdefault") {
                            // Maxres returns a 120×90 placeholder if the actual HD doesn't exist
                            setMaxresAvailable(e.currentTarget.naturalWidth > 200);
                          }
                        }}
                        onError={() => {
                          if (q.key === "maxresdefault") setMaxresAvailable(false);
                        }}
                      />
                    </div>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="font-semibold">{q.label}</div>
                        <div className="text-sm text-muted-foreground font-mono">
                          {q.dimensions}
                        </div>
                      </div>
                      {isUnavailable && (
                        <span className="text-xs text-muted-foreground italic">not generated</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => downloadOne(q)}
                        disabled={isUnavailable}
                        className="flex-1"
                      >
                        <HiDownload className="w-4 h-4" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyUrl(url, q.key)}
                        aria-label="Copy thumbnail URL"
                      >
                        {copied === q.key ? (
                          <HiCheck className="w-4 h-4" />
                        ) : (
                          <HiClipboardCopy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {maxresAvailable === false && (
            <p className="text-sm text-muted-foreground">
              The 1280×720 maxres thumbnail isn&apos;t available for this video. YouTube only
              generates it for videos with sufficient source resolution.
            </p>
          )}
        </div>
      )}

      <div className="mt-16">
        <ToolsCustomDevCTA />
      </div>

      <div className="mt-16">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
      </div>
    </div>
  );
}
